import { zodResolver } from '@hookform/resolvers/zod';
import {
  DateInput,
  Dialog,
  Form,
  FormButton,
  useNotifications
} from '@ijoin/components';
import { usePostMapDefault } from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '../types';

type FormValues = {
  entryDate: string;
};

export const MapDefaultDialog: FC<{
  onClose: () => void;
  participantName: string;
  planEnrollmentId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: { entryDate: new Date().toISOString() },
    mode: 'all',
    resolver: zodResolver(
      z.object({ entryDate: z.string().min(1, 'Entry date is required') })
    )
  });

  const mapDefault = usePostMapDefault<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to default into MAP'),
      onSuccess: () => {
        queryClient.invalidateQueries();
        notifications.success(
          'Participant defaulted into MAP, analysis queued'
        );
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormValues) => {
    mapDefault.mutate({
      data: { entryDate: data.entryDate },
      planEnrollmentId: props.planEnrollmentId
    });
  };

  return (
    <Dialog
      data-testid='mapDefault'
      fullWidth
      isOpen
      maxWidth='xs'
      onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Default into MAP</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              {`${props.participantName} will be defaulted into the managed account program and sent through MAP analysis.`}
            </DialogContentText>
            <DateInput
              data-testid='mapDefaultEntryDate'
              fullWidth
              label='Entry Date'
              name='entryDate'
              required
              size='small'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelMapDefault'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='saveMapDefault'
            triggerValidation
            variant='contained'>
            Default into MAP
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

MapDefaultDialog.displayName = 'MapDefaultDialog';
